import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { useEffect } from 'react';
import { useNavigateTo } from '../../pages/redirect.hooks';

interface UrlOpenData {
  url: string;
}

/**
 * Manager used to keep one instance of a listener around appUrlOpen. Since we need
 * to register listeners in multiple places of our main App structure in order to use different
 * "history" instances that can push based on authenticated vs not authenticated. By leveraging
 * one active listener we can have components lower in the tree control the event opposed to an
 * instance higher in the tree
 */
class AppUrlListenerManager {
  private listener: PluginListenerHandle | undefined;

  public async register(callback: (data: UrlOpenData) => void): Promise<void> {
    this.remove();
    this.listener = await App.addListener('appUrlOpen', (data: UrlOpenData) => {
      callback(data);
    });
  }

  public remove(): void {
    this.listener?.remove();
  }
}

const appUrlListenerManager = new AppUrlListenerManager();

const AppUrlListener = (): null => {
  const [navigateTo] = useNavigateTo();

  useEffect(() => {
    appUrlListenerManager.register((data: UrlOpenData) => {
      if (data.url) {
        navigateTo({
          url: data.url,
        });
      }
    });

    return () => {
      appUrlListenerManager.remove();
    };
  }, [navigateTo]);

  return null;
};

export { AppUrlListener };
